/**
 * Scribe pipeline hook used by the intake forms (Clinical and CoCM).
 *
 * Owns the six pieces of state that the legacy `Encounter.form.tsx` kept
 * inline: manual-override, scribe status, run key, available compositions,
 * selected composition, and loading. Also owns the `applyScribePrefill`
 * callback that overlays parsed AICD composition data onto the form
 * without overwriting user edits.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturnType } from "@mantine/form";
import { trackAction, TelemetryActions } from "@/app/boot/telemetry";
import type {
  CallCompositionOption,
  EncounterBundleInput,
  ScribeFormShape,
} from "@benchmark/core/types";
import {
  MSE_PHONE_DEFAULTED_FIELD_KEYS,
  MSE_PHONE_NOT_INFERABLE_DEFAULT,
} from "@benchmark/core/constants";
import type { ScribeStatus } from "../Encounter.scribePrefill";

/**
 * Form shapes that compose the scribe pipeline. Anything that owns the
 * `ScribeFormShape` contract — the two intake form data shapes, plus
 * any future scribe-capable form — is acceptable.
 *
 * @agentic connections
 */
export type ScribeCapableFormData = ScribeFormShape;

/**
 * Manual re-polls within one encounter run before we emit a single
 * `friction.detected` RUM action. Tunable; see the plan's assumption A4.
 * @agentic connections
 */
const FRICTION_RETRY_THRESHOLD = 2;

/**
 * Return shape for {@link useScribePipeline}. The intake forms wire these
 * callbacks into `ScribePrefillRunner` + `ScribePrefillStatusUI` directly.
 *
 * @agentic connections
 */
export type UseScribePipelineResult = {
  manualOverride: boolean;
  scribeStatus: ScribeStatus;
  scribeRunKey: number;
  scribeLoading: boolean;
  availableCompositions: CallCompositionOption[];
  selectedCompositionId: string | null;
  setScribeLoading: (loading: boolean) => void;
  setScribeStatus: (status: ScribeStatus) => void;
  setAvailableCompositions: (options: CallCompositionOption[]) => void;
  setSelectedCompositionId: (id: string | null) => void;
  handleManualOverride: () => void;
  handleRetryPrefill: () => void;
  applyScribePrefill: (prefill: Partial<EncounterBundleInput>) => void;
};

/**
 * Initialises and exposes the scribe pipeline state + callbacks for an
 * intake typed form.
 *
 * @agentic connections
 */
export const useScribePipeline = <T extends ScribeCapableFormData>(
  form: UseFormReturnType<T>,
): UseScribePipelineResult => {
  const [manualOverride, setManualOverride] = useState(false);
  const [scribeStatus, setScribeStatus] = useState<ScribeStatus>("idle");
  const [scribeRunKey, setScribeRunKey] = useState(0);
  const [scribeLoading, setScribeLoading] = useState(false);
  const [availableCompositions, setAvailableCompositions] = useState<
    CallCompositionOption[]
  >([]);
  const [selectedCompositionId, setSelectedCompositionId] = useState<
    string | null
  >(null);

  // Friction tracking: clinicians who re-poll for an AICD composition that has
  // not been ingested yet click "Retry prefill" / "Refresh calls" repeatedly.
  // We count those manual re-polls and, past a threshold, emit a single
  // `friction.detected` RUM action so the team can recognize it in near real
  // time. See `apps/connections/docs/OBSERVABILITY.md`.
  const retryCountRef = useRef(0);
  const frictionEmittedRef = useRef(false);
  const hadCompositionsRef = useRef(false);
  const pipelineStartMsRef = useRef(0);

  // Set the run-start timestamp once on mount (kept out of render to satisfy
  // react-hooks/purity — `Date.now()` is impure).
  useEffect(() => {
    pipelineStartMsRef.current = Date.now();
  }, []);

  useEffect(() => {
    hadCompositionsRef.current = availableCompositions.length > 0;
  }, [availableCompositions]);

  // Reset the friction counter once a composition is successfully applied.
  useEffect(() => {
    if (scribeStatus === "applied") {
      retryCountRef.current = 0;
      frictionEmittedRef.current = false;
    }
  }, [scribeStatus]);

  const handleManualOverride = useCallback(() => {
    trackAction(TelemetryActions.ScribeManualOverride, {
      hadCompositions: hadCompositionsRef.current,
      retryCountBefore: retryCountRef.current,
    });
    retryCountRef.current = 0;
    frictionEmittedRef.current = false;
    setManualOverride(true);
    setScribeStatus("idle");
    setScribeLoading(false);
    setAvailableCompositions([]);
    setSelectedCompositionId(null);
    form.setFieldValue("_scribeEncounterId" as never, "" as never);
  }, [form]);

  const handleRetryPrefill = useCallback(() => {
    retryCountRef.current += 1;
    trackAction(TelemetryActions.ScribeManualRetry, {
      retryCount: retryCountRef.current,
      sinceStartMs: Date.now() - pipelineStartMsRef.current,
    });
    if (
      retryCountRef.current >= FRICTION_RETRY_THRESHOLD &&
      !frictionEmittedRef.current
    ) {
      frictionEmittedRef.current = true;
      trackAction(TelemetryActions.FrictionDetected, {
        flow: "scribe-prefill",
        retryCount: retryCountRef.current,
      });
    }
    form.setFieldValue("_scribeApplied" as never, false as never);
    form.setFieldValue("_scribeCompositionId" as never, "" as never);
    form.setFieldValue("_scribeEncounterId" as never, "" as never);
    setScribeStatus("loading");
    setScribeLoading(true);
    setManualOverride(false);
    setAvailableCompositions([]);
    setSelectedCompositionId(null);
    setScribeRunKey((k) => k + 1);
  }, [form]);

  const applyScribePrefill = useCallback(
    (prefill: Partial<EncounterBundleInput>) => {
      if (prefill.period?.start) {
        form.setFieldValue(
          "period.start" as never,
          prefill.period.start as never,
        );
      }
      if (prefill.period?.end) {
        form.setFieldValue("period.end" as never, prefill.period.end as never);
      }

      // NOTE: `comment` (the AI general note) is deliberately NOT prefilled.
      // The intake Notes step hides the General Note field (showGeneralNote
      // defaults false), so prefilling it would persist unreviewed AI
      // documentation. The intake narrative lives in the per-section fields.
      const simpleKeys: Array<keyof EncounterBundleInput> = [
        "encounter-inout",
        "encounter-method",
        "chief-complaint",
        "hpi-current-symptoms",
        "mental-health-history",
        "mental-status-appearance",
        "mental-status-behavior",
        "mental-status-speech",
        "mental-status-mood-affect",
        "mental-status-thought-process-content",
        "mental-status-perception",
        "mental-status-cognition",
        "mental-status-insight-judgment",
        "trauma-history",
        "medication-history",
        "family-history",
        "assessment-plan",
        // Universal Intake subsection fields (single-entry overhaul). The four
        // generic parents they replaced are deliberately absent — they are no
        // longer authored, only read back on historical encounters.
        "hospitalizations",
        "safety",
        "existing-treaters",
        "outpatient-care-history",
        "substance-current-use",
        "substance-treatment-history",
        "chronic-conditions",
        "other-physical-symptoms",
        "living-situation",
        "social-supports",
        "occupation-education",
        "peds-home-life",
        "peds-school-education",
        "peds-activities-strengths",
        "peds-peers-social",
        "peds-birth-complications",
        "peds-developmental-milestones",
        "peds-family-history",
      ];

      const phoneDefaultedKeys = new Set<string>(
        MSE_PHONE_DEFAULTED_FIELD_KEYS,
      );
      simpleKeys.forEach((key) => {
        const value = prefill[key] as string | undefined;
        if (!value) return;
        const current = (form.values as Record<string, unknown>)[
          key as string
        ] as string | undefined;
        const isSeededPhoneDefault =
          phoneDefaultedKeys.has(key as string) &&
          current?.trim() === MSE_PHONE_NOT_INFERABLE_DEFAULT;
        if (!current || current.trim() === "" || isSeededPhoneDefault) {
          form.setFieldValue(key as never, value as never);
        }
      });

      // Structured safety-risk triage signal (R1): carried onto the form so the
      // Safety Plan prompt keys off affirmed risk, not any `safety` prose.
      // Replace (never merge) the signal on every Composition application — a
      // newly selected call without the extension must clear a prior `true`
      // rather than leave it lingering.
      form.setFieldValue(
        "_aiSafetyRiskPresent" as never,
        (prefill._aiSafetyRiskPresent ?? false) as never,
      );

      form.setFieldValue("_scribeApplied" as never, true as never);
    },
    [form],
  );

  return {
    manualOverride,
    scribeStatus,
    scribeRunKey,
    scribeLoading,
    availableCompositions,
    selectedCompositionId,
    setScribeLoading,
    setScribeStatus,
    setAvailableCompositions,
    setSelectedCompositionId,
    handleManualOverride,
    handleRetryPrefill,
    applyScribePrefill,
  };
};
